import { Pencil, Trash2 } from "lucide-react";
import { IconButton } from "@/components/ui/IconButton";
import { LineItem } from "@/types/billing";
import { formatPLN } from "@/lib/format";
import { cn } from "@/lib/cn";

interface Props {
  items: LineItem[];
  onEdit?: (item: LineItem) => void;
  onDelete?: (item: LineItem) => void;
}

function num(v: string | null | undefined, digits = 2) {
  if (v == null || v === "") return "—";
  const n = parseFloat(v);
  return isNaN(n) ? "—" : n.toFixed(digits);
}

// Legacy sort order matching costcontrol.py _sort_records:
// rozl-zal-z-p.m. → utilities → CO offsets (by charge month) → CO settlement → zal-b.m. → czynsz
const TYPE_ORDER: Record<string, number> = {
  "rozl-zal-z-p.m.":       0,
  "en-el":                  1,
  "gaz":                    2,
  "zimna-woda":             3,
  "podgrzanie-cw":          4,
  "co":                     5,
  "oplata-stała-co-cw":     6,
  "smieci":                 7,
  "internet":               8,
  "odliczenie-zaliczki":    9,
  "rozliczenie-zaliczkowe": 10,
  "zal-b.m.":               11,
  "czynsz":                 12,
};

function sortItems(items: LineItem[]): LineItem[] {
  return [...items].sort((a, b) => {
    const oa = TYPE_ORDER[a.type] ?? 5;
    const ob = TYPE_ORDER[b.type] ?? 5;
    if (oa !== ob) return oa - ob;
    // Secondary: CO offsets sorted by charge month chronologically
    if (a.charge_year !== b.charge_year) return (a.charge_year ?? 0) - (b.charge_year ?? 0);
    return (a.charge_month ?? 0) - (b.charge_month ?? 0);
  });
}

export function LineItemsTable({ items, onEdit, onDelete }: Props) {
  if (items.length === 0) {
    return <p className="text-xs text-text-muted py-4">Brak pozycji.</p>;
  }
  const sorted = sortItems(items);
  return (
    <table className="w-full text-sm">
      <thead className="text-xs text-text-muted">
        <tr className="border-b border-border">
          <th className="text-left font-medium px-4 py-2">Pozycja</th>
          <th className="text-right font-medium px-4 py-2 hidden md:table-cell">Zużycie</th>
          <th className="text-right font-medium px-4 py-2 hidden md:table-cell">Na mieszk.</th>
          <th className="text-right font-medium px-4 py-2 hidden md:table-cell">Na osobę</th>
          <th className="text-right font-medium px-4 py-2">Do zapłaty</th>
          {(onEdit || onDelete) && <th className="px-4 py-2 w-20"></th>}
        </tr>
      </thead>
      <tbody>
        {sorted.map((it) => (
          <tr key={it.item_id} className={cn("border-b border-border last:border-0", !it.editable && "text-text-muted")}>
            <td className="px-4 py-2 text-xs">{it.description}</td>
            <td className="px-4 py-2 text-xs text-right font-mono hidden md:table-cell">{num(it.usage)}</td>
            <td className="px-4 py-2 text-xs text-right font-mono hidden md:table-cell">{num(it.cost_per_flat)}</td>
            <td className="px-4 py-2 text-xs text-right font-mono hidden md:table-cell">{num(it.cost_per_person)}</td>
            <td className="px-4 py-2 text-xs text-right font-mono">{formatPLN(it.amount)}</td>
            {(onEdit || onDelete) && (
              <td className="px-4 py-2 text-right">
                <div className="inline-flex gap-1">
                  {onEdit && (
                    <IconButton label="Edytuj" onClick={() => onEdit(it)}><Pencil className="size-3.5" /></IconButton>
                  )}
                  {onDelete && (
                    <IconButton label="Usuń" onClick={() => onDelete(it)}><Trash2 className="size-3.5" /></IconButton>
                  )}
                </div>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
